<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useArdhisasaTicketsStore } from 'dashboard/stores/ardhisasaTickets';

const emit = defineEmits(['authenticated', 'close']);

const { t } = useI18n();
const store = useArdhisasaTicketsStore();

const OTP_TTL_SECONDS = 5 * 60; // 5 minutes

const step = ref('credentials'); // 'credentials' | 'otp'
const username = ref('');
const password = ref('');
const otp = ref('');
const error = ref('');
const showPassword = ref(false);
const otpSecondsLeft = ref(0);

let otpTimer = null;

function startOtpCountdown() {
  otpSecondsLeft.value = OTP_TTL_SECONDS;
  clearInterval(otpTimer);
  otpTimer = setInterval(() => {
    if (otpSecondsLeft.value > 0) {
      otpSecondsLeft.value -= 1;
    } else {
      clearInterval(otpTimer);
    }
  }, 1000);
}

onUnmounted(() => clearInterval(otpTimer));

watch(step, val => {
  if (val === 'otp') startOtpCountdown();
  else clearInterval(otpTimer);
});

const otpCountdown = computed(() => {
  const m = Math.floor(otpSecondsLeft.value / 60);
  const s = otpSecondsLeft.value % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
});

const otpExpired = computed(
  () => otpSecondsLeft.value === 0 && step.value === 'otp'
);

async function submitCredentials() {
  error.value = '';
  try {
    await store.login(username.value, password.value);
    step.value = 'otp';
  } catch {
    error.value = t('ARDHISASA.AUTH.LOGIN_ERROR');
  }
}

async function submitOtp() {
  error.value = '';
  try {
    await store.verifyOtp(username.value, password.value, otp.value);
    emit('authenticated');
  } catch {
    error.value = t('ARDHISASA.AUTH.OTP_ERROR');
  }
}
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div
      class="bg-white dark:bg-n-solid-2 rounded-xl shadow-xl w-full max-w-sm p-6"
    >
      <h2 class="text-base font-semibold text-n-slate-12 mb-4">
        {{ t('ARDHISASA.AUTH.TITLE') }}
      </h2>

      <!-- Credentials step -->
      <form
        v-if="step === 'credentials'"
        class="flex flex-col gap-3"
        @submit.prevent="submitCredentials"
      >
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('ARDHISASA.AUTH.USERNAME') }}
          </label>
          <input
            v-model="username"
            type="text"
            required
            autocomplete="username"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('ARDHISASA.AUTH.PASSWORD') }}
          </label>
          <div class="relative">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              required
              autocomplete="current-password"
              class="w-full px-3 py-2 pr-9 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
            <button
              type="button"
              class="absolute inset-y-0 right-2 flex items-center text-n-slate-10 hover:text-n-slate-12"
              @click="showPassword = !showPassword"
            >
              <span
                :class="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                class="size-4"
              />
            </button>
          </div>
        </div>
        <p v-if="error" class="text-xs text-n-ruby-9">{{ error }}</p>
        <div class="flex gap-2 mt-1">
          <button
            type="button"
            class="flex-1 py-2 px-4 rounded-lg border border-n-weak text-sm text-n-slate-11 hover:bg-n-alpha-2"
            @click="emit('close')"
          >
            {{ t('ARDHISASA.AUTH.CANCEL') }}
          </button>
          <button
            type="submit"
            :disabled="store.uiFlags.loggingIn"
            class="flex-1 py-2 px-4 rounded-lg bg-[#71481d] text-white text-sm font-medium hover:bg-[#5a3915] disabled:opacity-50"
          >
            {{ store.uiFlags.loggingIn ? '...' : t('ARDHISASA.AUTH.LOGIN') }}
          </button>
        </div>
      </form>

      <!-- OTP step -->
      <form v-else class="flex flex-col gap-3" @submit.prevent="submitOtp">
        <div class="flex items-center justify-between">
          <p class="text-xs text-n-slate-11">
            {{ t('ARDHISASA.AUTH.OTP_HINT') }}
          </p>
          <span
            class="text-xs font-medium tabular-nums"
            :class="
              otpExpired
                ? 'text-n-ruby-9'
                : otpSecondsLeft <= 60
                  ? 'text-amber-500'
                  : 'text-n-slate-11'
            "
          >
            {{ otpExpired ? t('ARDHISASA.AUTH.OTP_EXPIRED') : otpCountdown }}
          </span>
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('ARDHISASA.AUTH.OTP_LABEL') }}
          </label>
          <input
            v-model="otp"
            type="text"
            required
            inputmode="numeric"
            :disabled="otpExpired"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand disabled:opacity-50"
          />
        </div>
        <p v-if="error" class="text-xs text-n-ruby-9">{{ error }}</p>
        <div class="flex gap-2">
          <button
            type="button"
            class="flex-1 py-2 px-4 rounded-lg border border-n-weak text-sm text-n-slate-11 hover:bg-n-alpha-2"
            @click="step = 'credentials'"
          >
            {{ t('ARDHISASA.AUTH.BACK') }}
          </button>
          <button
            type="submit"
            :disabled="store.uiFlags.verifyingOtp"
            class="flex-1 py-2 px-4 rounded-lg bg-[#71481d] text-white text-sm font-medium hover:bg-[#5a3915] disabled:opacity-50"
          >
            {{
              store.uiFlags.verifyingOtp ? '...' : t('ARDHISASA.AUTH.VERIFY')
            }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
